import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import isEmail from "validator/es/lib/isEmail";
import { apiFetch } from "./api-fetch";
import { businessPageBaseUrl as configuredBusinessPage, legacyRedirectUrl, lookUpCardByIds, openOnBusinessPage } from "./legacy-redirect";
import { Sentry } from "./sentry";
import FormContainer from "./components/FormContainer.jsx";
import SuccessMessage from "./components/SuccessMessage";
import InvalidLinkMessage from "./components/InvalidLinkMessage.jsx";
import OpenFailedMessage from "./components/OpenFailedMessage.jsx";
import "./index.css";

const App = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isFetchingGooglePass, setIsFetchingGooglePass] = useState(false);
  const [isFetchingApplePass, setIsFetchingApplePass] = useState(false);
  const [googlePassSaveLink, setGooglePassSaveLink] = useState(null);
  const [applePassDownloadUrl, setApplePassDownloadUrl] = useState(null);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [inAppBrowserName, setInAppBrowserName] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    passTemplateId: "",
    companyId: "",
  });
  const [errors, setErrors] = useState({
    firstName: false,
    // lastName: false,
    email: false,
  });
  const [alert, setAlert] = useState(null);
  const [deviceType, setDeviceType] = useState(null);
  const [invalidLink, setInvalidLink] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const location = useLocation();
  const { slug, shortCode } = useParams();
  const searchParams = new URLSearchParams(location.search);
  // Every card link opens the card's form on the Business Page, which asks the offers box: a new customer with no
  // answer is refused (F-818), and this form has none. It stays only where no Business Page is configured.
  const [isRedirecting, setIsRedirecting] = useState(() => {
    if (!configuredBusinessPage()) return false;
    const params = new URLSearchParams(location.search);
    if (params.get("companyId") && params.get("passTemplateId")) return true;
    return Boolean(slug && shortCode && !params.get("companyId"));
  });
  // Where the card opens on the Business Page; after 3 s still here, a plain link to it shows under the spinner.
  const [redirectTarget, setRedirectTarget] = useState(null);
  const [showOpenLink, setShowOpenLink] = useState(false);
  // The old ids link's lookup got no answer, twice: the person can try again.
  const [openFailed, setOpenFailed] = useState(false);
  const [lookupRound, setLookupRound] = useState(0);
  const [loadingText, setLoadingText] = useState("");
  const autoSubmittedRef = useRef(false);

  // CRITICAL: Legacy URL support — DO NOT REMOVE
  // Old links (favecard-prod-installpass.web.app/?passTemplateId=X&companyId=Y)
  // are permanently in the wild (printed QR codes, Instagram bios).
  const passTemplateIdFromQuery = searchParams.get("passTemplateId");
  const companyIdFromQuery = searchParams.get("companyId");
  const isDemo = searchParams.get("demo") === "true";
  const prefillEmail = searchParams.get("email");
  const prefillFirstName = searchParams.get("firstName");

  const [resolvedIds, setResolvedIds] = useState({
    passTemplateId: passTemplateIdFromQuery || "",
    companyId: companyIdFromQuery || "",
  });

  const passTemplateId = resolvedIds.passTemplateId;
  const companyId = resolvedIds.companyId;

  const functionsBaseUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_BASE_URL;
  const businessPageBaseUrl = configuredBusinessPage();
  // A card or ids link with a Business Page always leaves for it: this form never signs anyone up from it.
  const leavesForBusinessPage = Boolean(businessPageBaseUrl) && Boolean(
    (companyIdFromQuery && passTemplateIdFromQuery) || (slug && shortCode && !companyIdFromQuery),
  );
  const generatePassApiUrl = `${functionsBaseUrl}/api/v1/companies/${companyId}/pass-templates/${passTemplateId}/passes`;

  //const progressPercent = success ? 100 : 50;

  // Get current URL for QR Code
  const currentUrl = window.location.href;

  // Slug without shortCode = incomplete link (business page URL, not card form URL)
  useEffect(() => {
    if (slug && !shortCode && !companyIdFromQuery) {
      setInvalidLink(true);
    }
  }, [slug, shortCode, companyIdFromQuery]);

  // A card link names its Business Page itself: open it there, prefill and source codes kept.
  useEffect(() => {
    if (!slug || !shortCode || companyIdFromQuery || !businessPageBaseUrl) return;
    setRedirectTarget(legacyRedirectUrl(businessPageBaseUrl, slug, shortCode, location.search));
  }, [slug, shortCode, companyIdFromQuery, businessPageBaseUrl, location.search]);

  // Leave for the card on the Business Page. If the page is still here after 3 s, the move was slow or blocked: the
  // spinner stays, with a plain link to the card. This form never takes over: it cannot ask the offers box (F-818).
  useEffect(() => {
    if (!redirectTarget) return undefined;
    openOnBusinessPage(redirectTarget);
    const timer = setTimeout(() => setShowOpenLink(true), 3000);
    return () => clearTimeout(timer);
  }, [redirectTarget]);

  // Resolve slug/shortCode to companyId/passTemplateId via API (only without a Business Page)
  useEffect(() => {
    if (slug && shortCode && !companyIdFromQuery && !businessPageBaseUrl) {
      setIsResolving(true);
      fetch(`${functionsBaseUrl}/api/v1/resolve/${slug}/${shortCode}`)
        .then((res) => {
          if (!res.ok) throw new Error("Resolve failed");
          return res.json();
        })
        .then((data) => {
          setResolvedIds({
            companyId: data.companyId,
            passTemplateId: data.passTemplateId,
          });
        })
        .catch(() => {
          setInvalidLink(true);
        })
        .finally(() => {
          setIsResolving(false);
        });
    }
  }, [slug, shortCode, companyIdFromQuery, functionsBaseUrl, businessPageBaseUrl]);

  // Redirect legacy URLs (?companyId=X&passTemplateId=Y) to business-page-next
  useEffect(() => {
    if (!companyIdFromQuery || !passTemplateIdFromQuery || !businessPageBaseUrl) return undefined;
    let cancelled = false;
    setIsRedirecting(true);
    setOpenFailed(false);
    lookUpCardByIds(
      `${functionsBaseUrl}/api/v1/resolve/by-ids?companyId=${encodeURIComponent(companyIdFromQuery)}&passTemplateId=${encodeURIComponent(passTemplateIdFromQuery)}`,
    ).then((card) => {
      if (cancelled) return;
      if (card.kind === "found") {
        setRedirectTarget(legacyRedirectUrl(businessPageBaseUrl, card.slug, card.shortCode, location.search));
        return;
      }
      // No card to open (404): say so. No answer: let them try again. Never this form: it cannot take a new customer
      // without the offers box (F-818), nor a known one without an e-mail code (#861).
      setIsRedirecting(false);
      if (card.kind === "missing") setInvalidLink(true);
      else setOpenFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [companyIdFromQuery, passTemplateIdFromQuery, businessPageBaseUrl, functionsBaseUrl, location.search, lookupRound]);

  const retryOpening = () => {
    setOpenFailed(false);
    setIsRedirecting(true);
    setLookupRound((round) => round + 1);
  };

  // Validate — must have IDs (from query params or resolved)
  useEffect(() => {
    // Skip validation while resolving slug
    if (isResolving) return;

    // After resolution or from query params: must have both IDs
    if (!passTemplateId || !companyId) {
      // Still waiting for slug resolution to populate resolvedIds
      if (slug && shortCode && !resolvedIds.companyId) return;
      setInvalidLink(true);
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType("apple");
    } else if (/android/.test(userAgent)) {
      setDeviceType("android");
    } else if (
      /macintosh/.test(userAgent) &&
      !/iphone|ipad|ipod/.test(userAgent)
    ) {
      setDeviceType("macos");
    } else {
      setDeviceType("other");
    }

  }, [passTemplateId, companyId, isResolving, slug, shortCode, resolvedIds.companyId]);

  // Detect in-app browsers on iOS (separate effect — must always run on mount)
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iphone|ipad|ipod/i.test(ua)) {
      const isInApp = /FBAN|FBAV|Instagram|TikTok|BytedanceWebview|Line\/|Snapchat|Twitter|LinkedInApp|Pinterest|MicroMessenger|Reddit/i.test(ua);
      setIsInAppBrowser(isInApp);
      if (isInApp) {
        const browserName = /FBAN|FBAV/i.test(ua) ? "facebook"
          : /Instagram/i.test(ua) ? "instagram"
          : /TikTok|BytedanceWebview/i.test(ua) ? "tiktok"
          : /Snapchat/i.test(ua) ? "snapchat"
          : /Twitter/i.test(ua) ? "twitter"
          : /LinkedInApp/i.test(ua) ? "linkedin"
          : "other_in_app";
        setInAppBrowserName(browserName);
        try {
          Sentry.captureMessage(`In-app browser detected: ${browserName}`, "warning");
        } catch { /* */ }
      }
    }
  }, []);

  // Update formData when IDs are available (from query params or resolved)
  useEffect(() => {
    if (passTemplateId && companyId) {
      setFormData((prevData) => ({
        ...prevData,
        passTemplateId,
        companyId,
      }));
    }
  }, [passTemplateId, companyId]);

  // Set page title based on detected language
  useEffect(() => {
    document.title = t("app.page_title");
    document.documentElement.lang = i18n.language;
  }, [i18n.language, t]);

  // Progressive loading messages — cycle through steps as time passes
  useEffect(() => {
    if (!loading) {
      setLoadingText(t("form_container.loading_step_1"));
      return;
    }
    setLoadingText(t("form_container.loading_step_1"));
    const steps = [
      { key: "form_container.loading_step_2", delay: 3000 },
      { key: "form_container.loading_step_3", delay: 7000 },
      { key: "form_container.loading_step_4", delay: 12000 },
    ];
    const timers = steps.map(step =>
      setTimeout(() => setLoadingText(t(step.key)), step.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [loading, t]);

  // Auto-submit when email + firstName are pre-filled from QR URL (mobile only)
  useEffect(() => {
    if (
      isRedirecting ||
      leavesForBusinessPage ||
      autoSubmittedRef.current ||
      !prefillEmail ||
      !prefillFirstName ||
      !passTemplateId ||
      !companyId ||
      !deviceType ||
      (deviceType !== "apple" && deviceType !== "android")
    ) {
      return;
    }

    autoSubmittedRef.current = true;
    setAutoSubmitting(true);

    // Pre-fill form data so if auto-submit fails, user sees a filled form
    const autoFormData = {
      email: prefillEmail,
      firstName: prefillFirstName,
      lastName: "",
      passTemplateId,
      companyId,
    };
    setFormData(autoFormData);

    // Auto-submit directly with the known data
    const platform = deviceType === "android" ? "google" : "apple";
    const submitAutoForm = async () => {
      try {
        setLoading(true);
        setAlert(null);
        const response = await apiFetch(generatePassApiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...autoFormData, platform }),
        });

        if (platform === "google") {
          if (!response.ok) throw new Error("Failed");
          const data = await response.json();
          if (data.saveLink) {
            setGooglePassSaveLink(data.saveLink);
            setSuccess(true);
          } else {
            throw new Error("No saveLink");
          }
        } else {
          if (!response.ok) throw new Error("Failed");
          const data = await response.json();
          if (!data.downloadUrl) throw new Error("No downloadUrl");
          setApplePassDownloadUrl(data.downloadUrl);
          setSuccess(true);
        }
      } catch (error) {
        console.error("Auto-submit failed:", error);
        try { Sentry.captureException(error, { extra: { context: "auto-submit" } }); } catch { /* */ }
        setAlert({
          type: "error",
          message: t("app.errors.generic_error"),
        });
      } finally {
        setLoading(false);
        setAutoSubmitting(false);
      }
    };

    submitAutoForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirecting, leavesForBusinessPage, deviceType, prefillEmail, prefillFirstName, passTemplateId, companyId]);

  //Scroll to the top of the page when success is true
  useEffect(() => {
    if (success) {
      window.scrollTo(0, 0);
    }
  }, [success]);

  const validateForm = () => {
    const newErrors = {
      firstName: false,
      // lastName: false,
      email: false,
    };

    // Name validation
    if (!formData.firstName) {
      newErrors.firstName = t("app.validation.required");
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = t("app.validation.required");
    } else if (!isEmail(formData.email)) {
      newErrors.email = t("app.validation.email_invalid");
    }

    setErrors(newErrors);

    // Check if there are any errors
    return !newErrors.firstName /*&& !newErrors.lastName*/ && !newErrors.email;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Reset error for this field during editing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleClearInput = (fieldName) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: "" }));
    setErrors((prev) => ({ ...prev, [fieldName]: false }));
  };

  const fetchAndStoreGooglePassLink = () => {
    setIsFetchingGooglePass(true);
    setLoading(true);
    setAlert(null);

    apiFetch(generatePassApiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, platform: "google" }),
    })
      .then((response) => {
        if (!response.ok) {
          return response
            .json()
            .then((errData) => {
              throw new Error(
                errData.error || errData.message || t("app.errors.google_pass_link_failed")
              );
            })
            .catch((e) => {
              if (e instanceof Error && e.message) throw e;
              throw new Error(t("app.errors.google_pass_link_failed"));
            });
        }
        return response.json();
      })
      .then((data) => {
        if (data.saveLink) {
          setGooglePassSaveLink(data.saveLink);
          setSuccess(true);
        } else {
          throw new Error(t("app.errors.google_pass_save_link_failed"));
        }
        setIsFetchingGooglePass(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching Google Pass link:", error);
        try { Sentry.captureException(error, { extra: { context: "fetchGooglePassLink" } }); } catch { /* */ }
        setIsFetchingGooglePass(false);
        setLoading(false);
        setAlert({
          type: "error",
          message:
            error.message ||
            t("app.errors.generic_error"),
        });
        setSuccess(false);
      });
  };

  const fetchAndStoreApplePass = () => {
    setIsFetchingApplePass(true);
    setLoading(true);
    setAlert(null);

    apiFetch(generatePassApiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, platform: "apple" }),
    })
      .then((response) => {
        if (!response.ok) {
          return response
            .text()
            .then((text) => {
              let msg = t("app.errors.apple_pass_failed");
              try { msg = JSON.parse(text).message || msg; } catch { /* */ }
              throw new Error(msg);
            });
        }
        return response.json();
      })
      .then((data) => {
        if (!data.downloadUrl) throw new Error(t("app.errors.apple_pass_failed"));
        setApplePassDownloadUrl(data.downloadUrl);
        setSuccess(true);
        setIsFetchingApplePass(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching Apple Pass:", error);
        try { Sentry.captureException(error, { extra: { context: "fetchApplePass" } }); } catch { /* */ }
        setIsFetchingApplePass(false);
        setLoading(false);
        const userMessage = error instanceof SyntaxError
          ? t("app.errors.apple_pass_failed")
          : (error.message || t("app.errors.generic_error"));
        setAlert({ type: "error", message: userMessage });
        setSuccess(false);
      });
  };

  const handleInstallGooglePass = () => {
    if (!googlePassSaveLink) {
      console.error("Google Pass save link is not available.");
      return;
    }
    window.location.href = googlePassSaveLink;
  };

  const handleInstallApplePass = () => {
    if (!applePassDownloadUrl) return;
    // Safari intercepts application/vnd.apple.pkpass and shows the Wallet sheet
    // as an overlay, then returns to this page. In-app browsers may navigate away
    // (blank page after), but InAppBrowserNotice provides a copy-link fallback.
    window.location.href = applePassDownloadUrl;
  };

  const handleButtonClick = () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setLoading(true);

    if (deviceType === "android") {
      fetchAndStoreGooglePassLink();
    } else if (deviceType === "apple") {
      fetchAndStoreApplePass();
    }
  };

  if (isRedirecting) {
    return (
      <div className="bg-gray-50 min-h-screen flex flex-col items-center justify-center gap-6 px-4">
        <div role="status" aria-label={t("primary_button.loading_fallback")} className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        {showOpenLink && redirectTarget && (
          <a href={redirectTarget} className="text-base text-slate-700 underline underline-offset-4">
            {t("form_container.submit_button")}
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col items-center py-8 px-4">
      <div className="relative overflow-hidden bg-white p-6 rounded-lg shadow-md w-full max-w-md">
        <>
          {/* <div className="absolute w-96 h-96 blur-2xl z-1 bg-gradient-radial from-teal-100 right-[-5rem] top-[5rem]"></div>
          <div className="absolute w-96 h-96 blur-2xl z-1 bg-gradient-radial from-rose-100 left-[-5rem] top-[10rem]"></div> */}
          {deviceType === "macos" || deviceType === "other" ? (
            <div className="text-center relative z-10">
              <h2 className="text-2xl text-slate-700 mb-2 font-heading">
                {t("app.desktop_view.title")}
              </h2>
              <p className="mb-6 text-slate-600">
                {t("app.desktop_view.description")}
              </p>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg inline-block">
                  <QRCode value={currentUrl} size={256} />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* {!invalidLink && (
                <div className="mb-6 w-full">
                  <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`bg-lime-300 h-2 rounded-full transition-all duration-500 ease-in-out ${
                        success ? "w-full" : "w-1/2"
                      }`}
                      role="progressbar"
                      aria-valuenow={progressPercent}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                </div>
              )} */}

              {isResolving ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : invalidLink ? (
                <InvalidLinkMessage />
              ) : openFailed ? (
                <OpenFailedMessage onRetry={retryOpening} />
              ) : autoSubmitting ? (
                <div className="flex flex-col items-center gap-4 py-8 relative z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-slate-600">{t("app.auto_submit_loading", "Creating your card...")}</p>
                </div>
              ) : success ? (
                <SuccessMessage
                  deviceType={deviceType}
                  onInstallGooglePass={handleInstallGooglePass}
                  isFetchingGooglePass={isFetchingGooglePass}
                  googlePassReady={!!googlePassSaveLink}
                  onInstallApplePass={handleInstallApplePass}
                  isFetchingApplePass={isFetchingApplePass}
                  applePassReady={!!applePassDownloadUrl}
                  isInAppBrowser={isInAppBrowser}
                  browserName={inAppBrowserName}
                  applePassDownloadUrl={applePassDownloadUrl}
                  firstName={formData.firstName}
                  isDemo={isDemo}
                  companyId={companyId}
                  passTemplateId={passTemplateId}
                />
              ) : (
                <FormContainer
                  formData={formData}
                  errors={errors}
                  alert={alert}
                  loading={loading}
                  loadingText={loadingText}
                  handleInputChange={handleInputChange}
                  handleClearInput={handleClearInput}
                  handleButtonClick={handleButtonClick}
                  setAlert={setAlert}
                />
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default App;
